import { rainbowColors } from "@/pages/Dashboard/ComparisonTools";
import { colorSchemaList } from "@/utils/colorSchemas";
import { drawChartJsImage } from "@/utils/globalUtils";
import { BarElement, CategoryScale, Chart as ChartJS, Legend, Title, Tooltip, LinearScale } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import { VscRefresh } from "react-icons/vsc";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, annotationPlugin, zoomPlugin);

const unitOptions = [
  { label: "minute", value: "" },
  { label: "minute", value: 1 },
  { label: "hour", value: 60 },
  { label: "day", value: 1440 },
  { label: "month", value: 43830 },
  { label: "year", value: 525960 },
];

const hexToRgb = (hex) => {
  const bigint = parseInt(hex.replace("#", ""), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r}, ${g}, ${b}`;
};

const alignItems = {
  left: "start",
  center: "center",
  right: "end",
};

const ChartJsBarChart = ({
  data = [],
  colorSchema,
  dateFormat = "D/M/YY hh:mm a",
  gridLine = true,
  verticalLine = true,
  legendPosition = "center bottom",
  threshold = [],
  showThumbsUp = false,
  tickGaps = 1,
  intervalTime,
}) => {
  const chartRef = useRef(null);
  const chartContainerRef = useRef(null);

  const [chartData, setChartData] = useState({ datasets: [] });

  const annotations = threshold.reduce((acc, { value, color }, index) => {
    if (value && color) {
      acc[`line${index}`] = {
        type: "line",
        yMin: value,
        yMax: value,
        borderColor: color,
        borderWidth: 2,
      };
    }
    return acc;
  }, {});

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 50,
      },
    },
    scales: {
      x: {
        type: "time",
        time: {
          unit: unitOptions.find(({ value }) => value === intervalTime)?.label || "day",
          // stepSize: Number(tickGaps) * Number(intervalValue),
          tooltipFormat: "DD/MM/YYYY hh:mm a",
          displayFormats: {
            hour: "DD/MM/YYYY hh:mm a",
          },
        },
        position: "bottom",
        grid: {
          display: false,
        },
        border: {
          display: verticalLine,
        },
        ticks: {
          source: "data",
          autoSkip: false,
          callback(value, index) {
            const customGap = Number(tickGaps) || 1;
            return index % customGap === 0
              ? moment(value).format(dateFormat) // 👈 VALUE, not label
              : "";
          },
          // callback: function (value, index) {
          //   const label = this.getLabelForValue(value);
          //   if (typeof Number(tickGaps) === "number" && tickGaps > 0) {
          //     return index % tickGaps === 0 ? moment(label, "DD/MM/YYYY HH:mm a").format(dateFormat) : undefined;
          //   }
          //   return index % 2 === 0 ? moment(label, "DD/MM/YYYY HH:mm a").format(dateFormat) : undefined;
          // },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: gridLine,
        },
        border: {
          display: verticalLine,
        },
      },
    },
    plugins: {
      annotation: { annotations },
      legend: {
        align: alignItems[legendPosition.split(" ")[0]] || "center",
        position: legendPosition.split(" ")[1] || "bottom",
      },
      zoom: {
        pan: {
          enabled: true,
          mode: "x", // x, y, or xy
        },
        zoom: {
          // wheel: {
          //   enabled: true,
          // },
          drag: {
            enabled: true,
            backgroundColor: "rgba(0,0,0,0.1)",
            borderColor: "#000",
            borderWidth: 1,
          },
          pinch: {
            enabled: true,
          },
          mode: "x",
        },
      },
    },
    animation: {
      onComplete: function ({ chart }) {
        if (showThumbsUp) {
          drawChartJsImage(chart);
        }
      },
      afterDraw: function ({ chart }) {
        if (showThumbsUp) {
          drawChartJsImage(chart);
        }
      },
    },
  };

  useEffect(() => {
    const scheme = colorSchemaList.others?.find((item) => item?.value === colorSchema);
    const colors = scheme?.colors || rainbowColors;

    const chartInstance = chartRef.current;
    if (chartInstance) {
      const ctx = chartInstance.ctx;

      if (Array.isArray(data)) {
        const datasets = data.map(({ chart, name, _id, color }, index) => {
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          const baseColor = color || colors[index % colors.length];

          gradient.addColorStop(0, `rgba(${hexToRgb(baseColor)}, 0.2)`);
          gradient.addColorStop(1, `rgba(${hexToRgb(baseColor)}, 1)`);

          return {
            label: name,
            data: Array.isArray(chart) ? chart.map((item) => ({ ...item, x: new Date(item.x) })) : [],
            // data: Array.isArray(chart)
            //   ? chart.map((item) => ({ ...item, x: moment(item?.x).format("DD/MM/YYYY HH:mm a") }))
            //   : [],

            _id,
            backgroundColor: gradient,
            borderColor: baseColor,
            borderWidth: 1,
          };
        });
        setChartData({ datasets });
      }
    }
  }, [data, colorSchema]);

  const handleResetZoom = () => {
    chartRef.current.resetZoom();
  };

  return (
    <>
      <div ref={chartContainerRef} style={{ position: "relative", width: "100%", height: "calc(100%  - 30px)" }}>
        <div className="position-absolute r-0 top-0 border border-primary px-3 cursor-pointer layout-controls" onClick={handleResetZoom}>
          <VscRefresh
            style={{
              fontWidth: "bold",
              fontSize: "20",
            }}
          />
        </div>
        <Bar ref={chartRef} data={chartData} options={options} />
      </div>
    </>
  );
};

export default ChartJsBarChart;

ChartJsBarChart.propTypes = {
  data: PropTypes.array.isRequired,
  colorSchema: PropTypes.string,
  dateFormat: PropTypes.string,
  gridLine: PropTypes.bool,
  verticalLine: PropTypes.bool,
  legendPosition: PropTypes.string,
  threshold: PropTypes.array,
  showThumbsUp: PropTypes.bool,
  tickGaps: PropTypes.number,
  intervalTime: PropTypes.number || PropTypes.string,
  intervalValue: PropTypes.number,
};
